import { Component, HostListener, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";

import { NgFor, NgIf, NgStyle, SlicePipe } from "@angular/common";

import { ActivatedRoute, ParamMap, RouterLink } from "@angular/router";

import { HttpClient } from "@angular/common/http";

import { catchError, of } from "rxjs";

import { GloPaginationComponent } from "../../../../shared/components/pagination/pagination.component";

import { GloTagComponent } from "../../../../shared/components/tag/tag.component";

import { GloControlComponent } from "../../../../shared/components/glo-control/glo-control.component";

import { GloDateControl } from "../../../../shared/components/glo-date-control/glo-date-control.component";

import { GloTimepickerComponent } from "../../../../shared/glo-timepicker/glo-timepicker.component";

import { GoogleMapComponent } from "../google-map/google-map.component";

import { environment } from "../../../../../environments/environment";

import { Salon } from "../../../../shared/models/salon.model";
import { API_ENDPOINTS } from "../../../../shared/services/api/api-endpoints";
import { GoogleAddressComponent } from "../../../../shared/google-address/google-address.component";

type SearchSuggestionKind = "salon" | "service" | "type" | "tag";

interface SearchSuggestion {
  label: string;

  value: string;

  kind: SearchSuggestionKind;
}

@Component({
  selector: "app-search",

  standalone: true,

  imports: [
    NgIf,
    NgFor,
    NgStyle,
    SlicePipe,

    FormsModule,

    RouterLink,

    GloPaginationComponent,
    GloTagComponent,
    GloControlComponent,

    GloDateControl,
    GloTimepickerComponent,

    GoogleMapComponent,
    GoogleAddressComponent,
  ],

  templateUrl: "./search.component.html",

  styleUrl: "./search.component.scss",
})
export class SearchComponent {
  private readonly route: ActivatedRoute = inject(ActivatedRoute);

  private readonly http: HttpClient = inject(HttpClient);

  assetUrl = environment.assetBaseUrl;

  apiUrl = environment.apiUrl;

  loading = true;

  error: string | null = null;

  serviceQuery = "";

  locationQuery = "";

  selectedSearchDate: string | null = null;

  selectedSearchStartTime: string | null = null;

  selectedSearchEndTime: string | null = null;

  // Cost filter options (1 = $ … 4 = $$$$). "" = Any price (no cost filter).
  costOptions = [
    { id: "", name: "Any price" },
    { id: "1", name: "$" },
    { id: "2", name: "$$" },
    { id: "3", name: "$$$" },
    { id: "4", name: "$$$$" },
  ];
  selectedCost = "";

  availabilitySearchActive = false;

  lastAvailabilityCity = "";

  lastAvailabilityDate = "";

  lastAvailabilityStartTime = "";

  lastAvailabilityEndTime = "";

  category: string | null = null;

  mapMarkers: {
    lat: number;
    lng: number;
  }[] = [];

  latitude = 53.3308;

  longitude = -6.2796;

  zoom = 14;

  bounds: google.maps.LatLngBoundsLiteral | null = null;

  currentPage = 1;

  pageSize = 8;

  shownSalons: any[] = [];

  salons: Salon[] = [];

  serviceSuggestions: SearchSuggestion[] = [];

  showServiceSuggestions = false;

  get effectiveLocationQuery(): string {
    const enteredLocation = this.locationQuery?.trim();

    if (enteredLocation) {
      return enteredLocation;
    }

    return this.getStoredCity();
  }

  get locationPlaceholder(): string {
    const storedCity = this.getStoredCity();

    return storedCity ? `Near ${storedCity}` : "City or location";
  }

  get availabilitySummary(): string {
    if (!this.availabilitySearchActive) {
      return "";
    }

    const timeRange =
      this.lastAvailabilityStartTime && this.lastAvailabilityEndTime
        ? `${this.formatTimeForDisplay(
            this.lastAvailabilityStartTime,
          )}–${this.formatTimeForDisplay(this.lastAvailabilityEndTime)}`
        : "";

    const parts = [
      this.lastAvailabilityCity,
      this.lastAvailabilityDate,
      timeRange,
    ].filter(Boolean);

    return parts.join(" · ");
  }

  get totalPages(): number {
    return Math.ceil(this.shownSalons.length / this.pageSize);
  }

  ngOnInit(): void {
    this.applyStoredMapCenter();

    this.route.queryParamMap.subscribe((params: ParamMap) => {
      const cat = params.get("category");
      const loc = params.get("location");
      const date = params.get("date");
      const startTime = params.get("start_time");
      const endTime = params.get("end_time");
      const cost = params.get("cost");

      this.category = cat ? this.decodeQueryParam(cat) : null;

      this.serviceQuery = this.category || "";

      // Location passed from landing page wins.
      // Otherwise use the city detected in the header.
      this.locationQuery = loc
        ? this.decodeQueryParam(loc)
        : this.getStoredCity();

      this.selectedSearchDate = date ? this.decodeQueryParam(date) : null;

      this.selectedSearchStartTime = startTime
        ? this.decodeQueryParam(startTime)
        : null;

      this.selectedSearchEndTime = endTime
        ? this.decodeQueryParam(endTime)
        : null;

      this.selectedCost = cost ? this.decodeQueryParam(cost) : "";

      this.currentPage = 1;

      // If salons are already loaded and query params change,
      // behave exactly like clicking Discover now.
      if (this.salons.length) {
        this.searchSalons();
      }
    });

    this.loadSalons();
  }

  loadSalons(): void {
    this.loading = true;

    this.error = null;

    const allSalons: Salon[] = [];

    const fetchPage = (url: string): void => {
      this.http
        .get<any>(url)
        .pipe(
          catchError((error) => {
            console.error("Error loading salons:", error);

            this.error = "Failed to load salons";

            this.loading = false;

            return of(null);
          }),
        )
        .subscribe((response) => {
          if (!response) {
            return;
          }

          const page = response?.results
            ? response
            : response?.data?.results
              ? response.data
              : null;

          if (!page) {
            console.error("Unexpected salon response shape:", response);

            this.error = "Unexpected salon response format";

            this.loading = false;

            return;
          }

          allSalons.push(...(page.results ?? []));

          if (page.next) {
            fetchPage(page.next);

            return;
          }

          this.salons = allSalons;

          this.currentPage = 1;

          this.searchSalons();
        });
    };

    fetchPage(`${this.apiUrl}/public/salons/`);
  }

  @HostListener("document:click")
  onDocumentClick(): void {
    this.showServiceSuggestions = false;
  }

  onServiceQueryInput(value: string): void {
    this.serviceQuery = value || "";

    this.category = this.serviceQuery.trim() || null;

    this.availabilitySearchActive = false;

    this.currentPage = 1;

    this.serviceSuggestions = this.buildServiceSuggestions(this.serviceQuery);

    this.showServiceSuggestions =
      !!this.serviceQuery.trim() && this.serviceSuggestions.length > 0;
  }

  selectServiceSuggestion(suggestion: SearchSuggestion): void {
    this.serviceQuery = suggestion.value;

    this.category = suggestion.value;

    this.availabilitySearchActive = false;

    this.showServiceSuggestions = false;

    this.currentPage = 1;
  }

  private buildServiceSuggestions(query: string): SearchSuggestion[] {
    const q = this.normalize(query);

    if (!q) {
      return [];
    }

    const seen = new Set<string>();

    const suggestions: (SearchSuggestion & {
      score: number;
    })[] = [];

    for (const salon of this.salons) {
      const salonName = salon?.name ?? "";

      const businessType =
        (salon as any)?.business_type ??
        (salon as any)?.businessType ??
        (salon as any)?.business?.business_type ??
        "";

      const services = this.getServiceNames(salon);

      const tags = this.tagsAsStrings((salon as any)?.tags);

      const candidates: {
        text: string;

        kind: SearchSuggestionKind;

        baseScore: number;
      }[] = [
        {
          text: salonName,

          kind: "salon",

          baseScore: 10,
        },

        {
          text: businessType,

          kind: "type",

          baseScore: 7,
        },

        ...services.map((service) => ({
          text: service,

          kind: "service" as const,

          baseScore: 8,
        })),

        ...tags.map((tag) => ({
          text: tag,

          kind: "tag" as const,

          baseScore: 6,
        })),
      ];

      for (const candidate of candidates) {
        const raw = candidate.text?.trim();

        if (!raw) {
          continue;
        }

        const normalized = this.normalize(raw);

        if (!normalized) {
          continue;
        }

        let score = 0;

        if (normalized === q) {
          score = candidate.baseScore + 20;
        } else if (normalized.startsWith(q)) {
          score = candidate.baseScore + 12;
        } else if (normalized.includes(q)) {
          score = candidate.baseScore + 7;
        } else {
          const queryTokens = this.tokenize(q);

          const candidateTokens = this.tokenize(normalized);

          const tokenHit = queryTokens.some((queryToken) =>
            candidateTokens.some((candidateToken) =>
              this.tokenMatches(queryToken, candidateToken),
            ),
          );

          if (tokenHit) {
            score = candidate.baseScore + 4;
          }
        }

        if (score <= 0) {
          continue;
        }

        const key = `${candidate.kind}::${normalized}`;

        if (seen.has(key)) {
          continue;
        }

        seen.add(key);

        suggestions.push({
          label: raw,

          value: raw,

          kind: candidate.kind,

          score,
        });
      }
    }

    return suggestions
      .sort((a, b) => b.score - a.score || a.label.localeCompare(b.label))
      .slice(0, 8)
      .map(({ label, value, kind }) => ({
        label,
        value,
        kind,
      }));
  }

  clearSearch(): void {
    this.serviceQuery = "";

    this.locationQuery = "";

    this.category = null;

    this.selectedSearchDate = null;

    this.selectedSearchStartTime = null;

    this.selectedSearchEndTime = null;

    this.availabilitySearchActive = false;

    this.lastAvailabilityCity = "";

    this.lastAvailabilityDate = "";

    this.lastAvailabilityStartTime = "";

    this.lastAvailabilityEndTime = "";

    this.serviceSuggestions = [];

    this.showServiceSuggestions = false;

    this.currentPage = 1;

    this.filterSalons();
  }

  onLocationQueryInput(): void {
    this.availabilitySearchActive = false;

    this.currentPage = 1;
  }

  onLocationPlaceSelected(place: google.maps.places.PlaceResult): void {
    const components = place.address_components ?? [];

    const city =
      components.find((component) => component.types.includes("locality"))
        ?.long_name ||
      components.find((component) => component.types.includes("postal_town"))
        ?.long_name ||
      components.find((component) => component.types.includes("sublocality"))
        ?.long_name ||
      components.find((component) =>
        component.types.includes("administrative_area_level_3"),
      )?.long_name ||
      place.name ||
      "";

    this.locationQuery = city.trim();

    this.availabilitySearchActive = false;
    this.currentPage = 1;

    if (typeof localStorage !== "undefined" && this.locationQuery) {
      localStorage.setItem("gloget_city", this.locationQuery);
    }

    const lat = place.geometry?.location?.lat?.();
    const lng = place.geometry?.location?.lng?.();

    if (
      typeof localStorage !== "undefined" &&
      typeof lat === "number" &&
      typeof lng === "number"
    ) {
      localStorage.setItem("gloget_latitude", String(lat));
      localStorage.setItem("gloget_longitude", String(lng));

      this.latitude = lat;
      this.longitude = lng;
      this.zoom = 14;
      this.bounds = null;
    }
  }

  onSearchDateChange(date: string): void {
    this.selectedSearchDate = date;

    this.availabilitySearchActive = false;
  }

  onSearchTimeChange(value: any): void {
    this.selectedSearchStartTime = this.normalizeSearchTime(
      value?.startTime ?? value?.start ?? null,
    );

    this.selectedSearchEndTime = this.normalizeSearchTime(
      value?.endTime ?? value?.end ?? null,
    );

    this.availabilitySearchActive = false;
  }

  searchSalons(): void {
    const service = (this.serviceQuery || this.category || "").trim();

    const city = this.effectiveLocationQuery.trim();

    const date = this.selectedSearchDate;

    const startTime = this.selectedSearchStartTime;

    const endTime = this.selectedSearchEndTime;

    this.currentPage = 1;

    this.showServiceSuggestions = false;

    // Availability filtering requires a date and a valid time range.
    // Until then, preserve the existing fast local filtering.
    if (!date || !startTime || !endTime) {
      this.availabilitySearchActive = false;

      this.filterSalons();

      return;
    }

    this.loading = true;

    this.error = null;

    const params: Record<string, string> = {
      date,
      start_time: startTime,
      end_time: endTime,
    };

    if (service) {
      params["service"] = service;
    }

    if (city) {
      params["city"] = city;
    }

    if (this.selectedCost) {
      params["cost"] = this.selectedCost;
    }

    // Only send the header-detected coordinates when the user has
    // not explicitly typed another location into the search field.
    if (!this.locationQuery.trim()) {
      const latitude = this.getStoredLatitude();

      const longitude = this.getStoredLongitude();

      if (latitude !== null && longitude !== null) {
        params["latitude"] = String(latitude);

        params["longitude"] = String(longitude);
      }
    }

    this.http
      .get<any>(API_ENDPOINTS.public.searchAvailability, { params })
      .pipe(
        catchError((error) => {
          console.error("Error searching salon availability:", error);

          this.error = "Unable to search availability right now.";

          this.loading = false;

          return of(null);
        }),
      )
      .subscribe((response) => {
        if (!response) {
          return;
        }

        const results =
          response?.results ??
          response?.data?.results ??
          response?.data ??
          response ??
          [];

        if (!Array.isArray(results)) {
          console.error("Unexpected availability search response:", response);

          this.error = "Unexpected availability search response.";

          this.loading = false;

          return;
        }

        this.shownSalons = results.filter((salon: any) =>
          this.matchesCostTier(salon),
        );

        this.availabilitySearchActive = true;

        this.lastAvailabilityCity = city;

        this.lastAvailabilityDate = date;

        this.lastAvailabilityStartTime = startTime;

        this.lastAvailabilityEndTime = endTime;

        this.currentPage = 1;

        this.rebuildMapMarkers();

        this.loading = false;
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;

    if (typeof window !== "undefined") {
      window.scrollTo({
        top: 420,

        behavior: "smooth",
      });
    }
  }

  filterSalons(): void {
    const serviceQ = this.normalize(this.serviceQuery || this.category || "");

    const locationQ = this.normalize(this.effectiveLocationQuery);

    if (!serviceQ && !locationQ && !this.selectedCost) {
      this.shownSalons = [...this.salons];

      this.currentPage = 1;

      this.rebuildMapMarkers();

      return;
    }

    const filtered = this.salons
      .filter((salon: any) => {
        const salonName = salon.name ?? "";

        const description = salon.description ?? "";

        const businessType =
          salon.business_type ??
          salon.businessType ??
          salon.business?.business_type ??
          "";

        const businessName =
          salon.business?.business_name ?? salon.business?.brand_name ?? "";

        const tags = this.tagsAsStrings(salon.tags);

        const services = this.getServiceNames(salon);

        const locations = this.getLocationValues(salon);

        // ---------------------------------------
        // Service / salon search
        //
        // ALL words typed by the user must
        // match somewhere.
        //
        // "test acc":
        // Default Test Account -> yes
        // Test Salon           -> no
        // ---------------------------------------

        let serviceMatched = true;

        if (serviceQ) {
          const serviceFields = [
            salonName,
            businessName,
            businessType,
            ...services,
            ...tags,
            description,
          ];

          serviceMatched = this.matchesAllQueryTokens(serviceQ, serviceFields);
        }

        // ---------------------------------------
        // Location
        //
        // All location words must match too.
        // ---------------------------------------

        let locationMatched = true;

        if (locationQ) {
          locationMatched = this.matchesAllQueryTokens(locationQ, locations);
        }

        return serviceMatched && locationMatched && this.matchesCostTier(salon);
      })
      .map((salon: any) => ({
        salon,

        score: this.scoreSalonMatch(salon, serviceQ, locationQ),
      }))
      .sort((a, b) => {
        // Ranking still happens AFTER
        // non-matches have been removed.

        if (b.score !== a.score) {
          return b.score - a.score;
        }

        const ratingA = Number(a.salon?.rating ?? 0);

        const ratingB = Number(b.salon?.rating ?? 0);

        if (ratingB !== ratingA) {
          return ratingB - ratingA;
        }

        const reviewsA = Number(a.salon?.review_count ?? 0);

        const reviewsB = Number(b.salon?.review_count ?? 0);

        return reviewsB - reviewsA;
      });

    this.shownSalons = filtered.map((item) => item.salon);

    this.currentPage = 1;

    this.rebuildMapMarkers();
  }

  // ---------------------------------------------------------
  // Cost tier helpers ($ … $$$$)
  //
  // A salon's tier is derived from its average service price:
  //   $    <  50
  //   $$   < 100
  //   $$$  < 200
  //   $$$$ >= 200
  // ---------------------------------------------------------
  private getSalonServicePrices(salon: any): number[] {
    const services = Array.isArray(salon?.services) ? salon.services : [];

    return services
      .map((service: any) =>
        Number.parseFloat(String(service?.price ?? "").replace(/[^0-9.]/g, "")),
      )
      .filter((price: number) => Number.isFinite(price) && price > 0);
  }

  private getSalonAvgPrice(salon: any): number {
    const prices = this.getSalonServicePrices(salon);

    if (!prices.length) {
      return 0;
    }

    const total = prices.reduce((sum: number, price: number) => sum + price, 0);

    return total / prices.length;
  }

  private costTierOf(salon: any): number {
    const price = this.getSalonAvgPrice(salon);

    if (price < 50) {
      return 1;
    }

    if (price < 100) {
      return 2;
    }

    if (price < 200) {
      return 3;
    }

    return 4;
  }

  private matchesCostTier(salon: any): boolean {
    if (!this.selectedCost) {
      return true;
    }

    return this.costTierOf(salon) === Number(this.selectedCost);
  }

  private rebuildMapMarkers(): void {
    this.mapMarkers = this.shownSalons
      .filter((salon: any) => salon.latitude && salon.longitude)
      .map((salon: any) => ({
        lat: Number(salon.latitude),

        lng: Number(salon.longitude),
      }));

    this.updateMapFromMarkers();
  }

  private updateMapFromMarkers(): void {
    const markers = this.mapMarkers;

    if (!markers.length) {
      this.bounds = null;

      this.zoom = 14;

      return;
    }

    if (markers.length === 1) {
      this.bounds = null;

      this.latitude = markers[0].lat;

      this.longitude = markers[0].lng;

      this.zoom = 14;

      return;
    }

    let minLat = markers[0].lat;

    let maxLat = markers[0].lat;

    let minLng = markers[0].lng;

    let maxLng = markers[0].lng;

    for (const marker of markers) {
      if (marker.lat < minLat) {
        minLat = marker.lat;
      }

      if (marker.lat > maxLat) {
        maxLat = marker.lat;
      }

      if (marker.lng < minLng) {
        minLng = marker.lng;
      }

      if (marker.lng > maxLng) {
        maxLng = marker.lng;
      }
    }

    const latPad = Math.max(
      (maxLat - minLat) * 0.12,

      0.002,
    );

    const lngPad = Math.max(
      (maxLng - minLng) * 0.12,

      0.002,
    );

    this.bounds = {
      north: maxLat + latPad,

      south: minLat - latPad,

      east: maxLng + lngPad,

      west: minLng - lngPad,
    };

    this.latitude = (this.bounds.north + this.bounds.south) / 2;

    this.longitude = (this.bounds.east + this.bounds.west) / 2;
  }

  private applyStoredMapCenter(): void {
    const latitude = this.getStoredLatitude();
    const longitude = this.getStoredLongitude();

    if (latitude === null || longitude === null) {
      return;
    }

    this.latitude = latitude;
    this.longitude = longitude;
  }

  private getStoredCity(): string {
    if (typeof window === "undefined" || typeof localStorage === "undefined") {
      return "";
    }

    return localStorage.getItem("gloget_city")?.trim() || "";
  }

  private getStoredLatitude(): number | null {
    if (typeof window === "undefined" || typeof localStorage === "undefined") {
      return null;
    }

    const raw = localStorage.getItem("gloget_latitude");

    if (!raw) {
      return null;
    }

    const value = Number(raw);

    return Number.isFinite(value) ? value : null;
  }

  private getStoredLongitude(): number | null {
    if (typeof window === "undefined" || typeof localStorage === "undefined") {
      return null;
    }

    const raw = localStorage.getItem("gloget_longitude");

    if (!raw) {
      return null;
    }

    const value = Number(raw);

    return Number.isFinite(value) ? value : null;
  }

  formatTimeForDisplay(time: string): string {
    if (!time) {
      return "";
    }

    const [hourText, minute = "00"] = time.split(":");

    let hour = Number(hourText);

    if (!Number.isFinite(hour)) {
      return time;
    }

    const suffix = hour >= 12 ? "PM" : "AM";

    hour = hour % 12 || 12;

    return `${hour}:${minute} ${suffix}`;
  }

  private normalizeSearchDate(value: any): string | null {
    if (!value) {
      return null;
    }

    if (typeof value === "string") {
      const trimmed = value.trim();

      const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);

      if (isoMatch) {
        return [
          isoMatch[1],
          isoMatch[2].padStart(2, "0"),
          isoMatch[3].padStart(2, "0"),
        ].join("-");
      }

      return trimmed || null;
    }

    if (typeof value === "object" && value.year && value.month && value.day) {
      return [
        String(value.year),
        String(value.month).padStart(2, "0"),
        String(value.day).padStart(2, "0"),
      ].join("-");
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return [
        value.getFullYear(),
        String(value.getMonth() + 1).padStart(2, "0"),
        String(value.getDate()).padStart(2, "0"),
      ].join("-");
    }

    return null;
  }

  private normalizeSearchTime(value: any): string | null {
    if (value == null || value === "") {
      return null;
    }

    if (typeof value === "object") {
      const hour =
        value.hour ?? value.hours ?? value.hour24 ?? value.selectedHour;

      const minute = value.minute ?? value.minutes ?? value.selectedMinute ?? 0;

      const meridian =
        value.meridian ?? value.period ?? value.ampm ?? value.amPm;

      if (hour !== undefined && hour !== null) {
        let h = Number(hour);
        const m = Number(minute) || 0;

        if (meridian) {
          const period = String(meridian).toUpperCase();

          if (period === "PM" && h < 12) {
            h += 12;
          }

          if (period === "AM" && h === 12) {
            h = 0;
          }
        }

        if (
          Number.isFinite(h) &&
          h >= 0 &&
          h <= 23 &&
          Number.isFinite(m) &&
          m >= 0 &&
          m <= 59
        ) {
          return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
        }
      }

      const nested =
        value.value ?? value.time ?? value.selectedTime ?? value.displayValue;

      if (nested !== undefined && nested !== value) {
        return this.normalizeSearchTime(nested);
      }

      return null;
    }

    const raw = String(value).trim();

    if (!raw) {
      return null;
    }

    const twentyFourHour = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);

    if (twentyFourHour) {
      const hour = Number(twentyFourHour[1]);
      const minute = Number(twentyFourHour[2]);

      if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
        return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
      }
    }

    const twelveHour = raw
      .toUpperCase()
      .match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/);

    if (twelveHour) {
      let hour = Number(twelveHour[1]);
      const minute = Number(twelveHour[2] || "00");
      const period = twelveHour[3];

      if (period === "PM" && hour < 12) {
        hour += 12;
      }

      if (period === "AM" && hour === 12) {
        hour = 0;
      }

      return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
    }

    return raw;
  }

  private normalize(value: any): string {
    return (value ?? "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/&/g, " and ")
      .replace(/[^\w\s-]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  private key(value: any): string {
    return this.normalize(value).replace(/[^a-z0-9]+/g, "");
  }

  private tokenize(value: any): string[] {
    return this.normalize(value)
      .split(" ")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  private decodeQueryParam(value: string): string {
    let result = value ?? "";

    try {
      result = decodeURIComponent(result);
    } catch {}

    return result.replace(/\+/g, " ").trim();
  }

  private tagText(tag: any): string {
    if (tag == null) {
      return "";
    }

    if (typeof tag === "string" || typeof tag === "number") {
      return String(tag);
    }

    if (typeof tag === "object") {
      return (
        tag.name ??
        tag.title ??
        tag.label ??
        tag.value ??
        tag.tag ??
        tag.text ??
        ""
      ).toString();
    }

    return "";
  }

  private tagsAsStrings(tags: any): string[] {
    if (!Array.isArray(tags)) {
      return [];
    }

    return tags.map((tag) => this.tagText(tag)).filter(Boolean);
  }

  private expandToken(token: string): string[] {
    const synonyms: Record<string, string[]> = {
      nail: ["nails", "manicure", "pedicure", "shellac", "gel", "acrylic"],

      nails: ["nail", "manicure", "pedicure", "shellac", "gel", "acrylic"],

      manicure: ["nail", "nails", "pedicure", "gel", "shellac"],

      pedicure: ["nail", "nails", "manicure", "foot care"],

      hair: ["haircut", "styling", "blowout", "color", "highlights", "salon"],

      haircut: ["hair", "trim", "styling", "barber"],

      barber: ["barbershop", "fade", "beard", "trim", "cut"],

      barbershop: ["barber", "fade", "beard", "trim", "cut"],

      spa: ["massage", "facial", "skincare", "relaxation"],

      massage: ["spa", "deep tissue", "relaxation"],

      facial: ["spa", "skincare", "skin"],

      brows: ["brow", "eyebrow", "eyebrows", "threading", "wax"],

      brow: ["brows", "eyebrow", "eyebrows", "threading", "wax"],

      lashes: ["lash", "eyelash", "extensions", "lift"],

      lash: ["lashes", "eyelash", "extensions", "lift"],

      waxing: ["wax", "hair removal"],

      toronto: [
        "gta",
        "downtown",
        "downtown toronto",
        "north york",
        "scarborough",
        "etobicoke",
      ],

      downtown: ["downtown toronto", "toronto"],
    };

    const variants = new Set<string>();

    variants.add(token);

    for (const item of synonyms[token] ?? []) {
      variants.add(item);
    }

    if (token.endsWith("s") && token.length > 3) {
      variants.add(token.slice(0, -1));
    } else {
      variants.add(`${token}s`);
    }

    if (token.endsWith("ing") && token.length > 5) {
      variants.add(token.slice(0, -3));
    }

    return [...variants].filter(Boolean);
  }

  private tokenMatches(
    token: string,

    candidate: string,
  ): boolean {
    if (!token || !candidate) {
      return false;
    }

    if (candidate === token) {
      return true;
    }

    if (candidate.includes(token)) {
      return true;
    }

    if (token.includes(candidate) && candidate.length >= 4) {
      return true;
    }

    const tokenStem = token.length >= 4 ? token.slice(0, 4) : token;

    const candidateStem =
      candidate.length >= 4 ? candidate.slice(0, 4) : candidate;

    return !!tokenStem && tokenStem === candidateStem;
  }

  private uniqueTokens(values: any[]): string[] {
    const tokens = values.flatMap((value) => this.tokenize(value));

    return [...new Set(tokens)];
  }

  private countTokenMatches(
    query: string,

    haystackValues: any[],
  ): number {
    const queryTokens = this.tokenize(query);

    if (!queryTokens.length) {
      return 0;
    }

    const haystackTokens = this.uniqueTokens(haystackValues);

    let matches = 0;

    for (const queryToken of queryTokens) {
      const variants = this.expandToken(queryToken);

      const matched = variants.some((variant) =>
        haystackTokens.some((candidate) =>
          this.tokenMatches(variant, candidate),
        ),
      );

      if (matched) {
        matches++;
      }
    }

    return matches;
  }

  private matchesAllQueryTokens(query: string, haystackValues: any[]): boolean {
    const queryTokens = this.tokenize(query);

    if (!queryTokens.length) {
      return true;
    }

    const haystackTokens = this.uniqueTokens(haystackValues);

    return queryTokens.every((queryToken) => {
      const variants = this.expandToken(queryToken);

      return variants.some((variant) =>
        haystackTokens.some((candidate) =>
          this.tokenMatches(variant, candidate),
        ),
      );
    });
  }

  private phraseBoost(
    query: string,

    values: any[],
  ): number {
    const normalizedQuery = this.normalize(query);

    if (!normalizedQuery) {
      return 0;
    }

    const joined = values.map((value) => this.normalize(value)).join(" | ");

    if (joined.includes(normalizedQuery)) {
      return 4;
    }

    const queryKey = this.key(normalizedQuery);

    const joinedKey = this.key(joined);

    if (queryKey && joinedKey.includes(queryKey)) {
      return 3;
    }

    return 0;
  }

  private getServiceNames(salon: any): string[] {
    if (!Array.isArray(salon?.services)) {
      return [];
    }

    return salon.services
      .map(
        (service: any) =>
          service?.name ?? service?.service_name ?? service?.title ?? service,
      )
      .filter(Boolean);
  }

  private getLocationValues(salon: any): string[] {
    return [
      salon.address,
      salon.address_1,
      salon.address_2,

      salon.city,

      salon.state,
      salon.province,

      salon.country,

      salon.postal_code ?? salon.postalCode,
    ].filter(Boolean);
  }

  private scoreSalonMatch(
    salon: any,

    serviceQuery: string,

    locationQuery: string,
  ): number {
    const salonName = salon.name ?? "";

    const description = salon.description ?? "";

    const businessType =
      salon.business_type ??
      salon.businessType ??
      salon.business?.business_type ??
      "";

    const businessName =
      salon.business?.business_name ?? salon.business?.brand_name ?? "";

    const tags = this.tagsAsStrings(salon.tags);

    const services = this.getServiceNames(salon);

    const locations = this.getLocationValues(salon);

    const strongServiceFields = [
      salonName,
      businessName,
      businessType,
      ...services,
      ...tags,
    ];

    const softServiceFields = [description];

    let score = 0;

    if (serviceQuery) {
      const strongHits = this.countTokenMatches(
        serviceQuery,
        strongServiceFields,
      );

      const softHits = this.countTokenMatches(serviceQuery, softServiceFields);

      score += strongHits * 5;

      score += softHits * 2;

      score += this.phraseBoost(serviceQuery, [
        ...strongServiceFields,
        ...softServiceFields,
      ]);

      if (this.normalize(salonName) === this.normalize(serviceQuery)) {
        score += 8;
      }

      if (this.normalize(businessName) === this.normalize(serviceQuery)) {
        score += 7;
      }

      if (this.normalize(businessType) === this.normalize(serviceQuery)) {
        score += 6;
      }
    }

    if (locationQuery) {
      const locationHits = this.countTokenMatches(locationQuery, locations);

      score += locationHits * 4;

      score += this.phraseBoost(locationQuery, locations);

      const city = this.normalize(salon.city);

      const locationNorm = this.normalize(locationQuery);

      if (city && city === locationNorm) {
        score += 6;
      }
    }

    if (serviceQuery && locationQuery) {
      const serviceMatched =
        this.countTokenMatches(serviceQuery, [
          ...strongServiceFields,
          ...softServiceFields,
        ]) > 0;

      const locationMatched =
        this.countTokenMatches(locationQuery, locations) > 0;

      if (serviceMatched && locationMatched) {
        score += 4;
      }
    }

    return score;
  }
}
